import { Component, computed, inject, input } from "@angular/core";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { Router } from "@angular/router";
import { ClickOutsideDirective } from "ev-directives";
import { environment } from "src/environments/environment";
import { MapDataService } from "src/services/map/map-data.service";

import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { switchMap } from "rxjs/operators";
import { of } from "rxjs";
import { INewDwellingDocumentInfo, INewDwellingInfo, INewDwellingProjectInfo, INewDwellingPublication } from "src/app/infopage/new-dwelling/newdwelling.model";
import { NewDwellingDataService } from "src/app/infopage/services/new-dwelling-data.service";

@Component({
    selector: "threedot-navigation",
    templateUrl: "./threedot-navigation.component.html",
    styleUrls: ["./threedot-navigation.component.scss"],
    imports: [ClickOutsideDirective],
})
export class ThreeDotNavigationComponent {
    dwelling = input<INewDwellingInfo>();
    newDwellingProject = input<INewDwellingProjectInfo>();
    publications = input<INewDwellingPublication[]>([]);
    mapMode = input<boolean>(false);

    private readonly authService = inject(EvAuthorizationService);
    private readonly newDwellingService = inject(NewDwellingDataService);
    private readonly mapDataService = inject(MapDataService);
    private readonly router = inject(Router);

    protected expanded: boolean = false;
    protected hasFavoriteListRole = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_FavoriteList));

    stages = computed(() => this.newDwellingProject()?.stageEstateTypesModel?.projectStageEstateTypes ?? []);

    projectLatLon = computed((): { lat: number; lon: number } | null => {
        if (!this.newDwellingProject()) {
            return null;
        }

        if (this.newDwellingProject().latitude != null && this.newDwellingProject().longitude != null) {
            return { lat: this.newDwellingProject().latitude, lon: this.newDwellingProject().longitude };
        }

        if (this.stages() && this.stages().length > 0) {
            const firstWithLatLon = this.stages().find((se) => se.latitude != null && se.longitude != null);
            if (firstWithLatLon) {
                return { lat: firstWithLatLon.latitude, lon: firstWithLatLon.longitude };
            }
        }

        return null;
    });

    mapLatLon = computed((): { lat: number; lon: number } | null => {
        if (this.dwelling()) {
            return { lat: this.dwelling().latitude, lon: this.dwelling().longitude };
        }

        return this.projectLatLon();
    });

    municipalityMapUrl = toSignal(
        toObservable(this.mapLatLon).pipe(
            switchMap((coords) =>
                coords?.lat != null && coords?.lon != null
                    ? this.mapDataService.getMunicipalityMapUrl(coords.lat, coords.lon, 18)
                    : of(null),
            ),
        ),
        { initialValue: null },
    );

    prospects = computed(() => this.dwelling()?.prospects ?? []);
    priceList = computed(() => this.dwelling()?.priceList ?? this.newDwellingProject()?.priceList ?? []);

    toggleExpanded(): void {
        this.expanded = !this.expanded;
    }
    closeDwellingDropDown(): void {
        this.expanded = false;
    }

    goToProjectSite(): void {
        let url = this.dwelling().prospectURL;
        if (!url.includes("https")) {
            url = "https://" + url;
        }
        window.open(url, "_blank");
    }

    navigateToNewDwellingOverview(): void {
        const pid = this.dwelling()?.projectId ?? this.newDwellingProject()?.unitId;
        this.router.navigate(["/infopage/newdwelling/project"], { queryParams: { pid } });
    }

    openProspect(): void {
        const isProd = typeof environment.isProdEnv === "string" ? environment.isProdEnv === "true" : !!environment.isProdEnv;
        const filename = isProd ? this.dwelling().pdfProspect : "Pro_Osl_Ullevål_Tårn.pdf";
        this.newDwellingService.getAndOpenNewDwellingProjectFile(filename);
    }

    openPricelist(): void {
        const isProd = typeof environment.isProdEnv === "string" ? environment.isProdEnv === "true" : !!environment.isProdEnv;
        const filename = isProd ? this.dwelling().pdfPriceList : "Pro_Osl_Ullevål_Tårn.pdf";
        this.newDwellingService.getAndOpenNewDwellingProjectFile(filename);
    }

    openFinnListing(): void {
        const finnUrl = "https://www.finn.no/" + this.dwelling().finnId;
        window.open(finnUrl, "_blank");
    }

    exportToExcel(): void {
        const workbookName = this.dwelling().projectName + "-" + this.dwelling().trimmedStageName;
        this.newDwellingService.getAndOpenNewDwellingUnitsExcel(this.dwelling().projectStageEstateTypeId, workbookName);
    }

    navigateToMapDwelling(): void {
        const lat = this.mapLatLon().lat;
        const lon = this.mapLatLon().lon;
        const pid = this.dwelling()?.projectId ?? this.newDwellingProject()?.unitId;
        const pseid = this.dwelling()?.projectStageEstateTypeId;
        const link = `/map/new-dwellings/market?lat=${lat}&lon=${lon}&pid=${pid}${pseid ? `&pseid=${pseid}` : ""}&zoom=16`;
        window.location.href = link;
    }

    goToMunicipalityMap(): void {
        if (this.municipalityMapUrl()) {
            window.open(this.municipalityMapUrl(), "_blank");
        }
    }

    openDocument(doc: INewDwellingDocumentInfo): void {
        this.newDwellingService.getAndOpenDocument(doc);
    }

    openPublication(url: string): void {
        if (!url.includes("https")) {
            url = "https://" + url;
        }
        window.open(url, "_blank");
    }
}
